import React, { useCallback, useState } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import {
  AllDoneFill,
  ArrowBackOutline,
  ArrowRightFill,
  EditOutline,
  FaceWithQuestionMark,
  HandWithCircleOutline,
  RoundedStar,
  TargetWithFlag,
} from '@lyearn/icons';
import { Button, Loader, Stack, Tabs, Typography } from '@lyearn/ui';
import usePageLayout from '@/components/PageLayout/usePageLayout';
import { PersonaCard } from '@/components/PersonaCard';
import { TypographyWithBulletPoint } from '@/components/TypographyWithBulletPoint';
import useNavigation from '@/hooks/useNavigation';
import { ContentsFragment } from '@/modules/corporate/modules/admin/graphql/fragments/generated/Contents';
import { useQuestionsQuery } from '@/modules/corporate/modules/questions/views/AddQuestionModal/graphql/queries/generated/questions';
import TemplatesHeaderNode from '@/modules/templates/views/components/TemplatesHeaderNode';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { UserSurveyResponseVersionStatusEnum } from '@/types/schema';

import SolutionCardGrid from '../../KnowledgeAssetManager/components/SolutionCardGrid';
import { useKnowledgeAssetsQuery } from '../../KnowledgeAssetManager/graphql/query/generated/knowledgeAssets';
import EmptyState from '../components/EmptyState';
import { usePersonaQuery } from '../graphql/queries/generated/persona';
import { usePersonaDetailsQuery } from '../graphql/queries/generated/personaDetails';
import { usePersonaQuestionIdsQuery } from '../graphql/queries/generated/personaQuestionIds';
import { DetailsPageProps } from '../types';

const parsePipeSeparatedString = (text: string): string[] => {
  if (!text) return [];
  return text
    .split('|')
    .map((item) => item.trim())
    .filter(Boolean);
};

const stripHtmlTags = (text: string): string => {
  if (!text) return '';
  return text.replace(/<\/?p>/g, '').trim();
};

const getResponseByQuestionText = (
  questionText: string,
  questions: any,
  responses: any,
  getIds?: boolean,
): string => {
  // Find the question with matching text
  const question = questions?.questions?.edges?.find(
    (edge: any) => edge?.node?.questionText == questionText,
  )?.node;

  if (!question) return '';

  // Get all responses from all edges
  const allResponses = responses?.userSurveyResponses?.edges?.map((edge: any) => edge?.node) || [];

  // Find the response for this question
  const response = allResponses.find((response: any) => response?.entityId === question?.id);

  if (getIds) {
    const ids =
      response?.surveyResponse?.optionResponse?.map((option: any) => option?.optionId) || [];
    return ids.join(', ');
  }

  const textResponse =
    response?.surveyResponse?.textResponse ||
    response?.surveyResponse?.optionResponse?.map((option: any) => option?.optionText).join(', ') ||
    response?.surveyResponse?.attachments?.map((attachment: any) => attachment?.url).join('|') ||
    '';
  return stripHtmlTags(textResponse);
};

interface CardData {
  title: string;
  responses: string[];
}

const PersonaDetails = ({ assetId, isPreview }: DetailsPageProps) => {
  const [currentTabId, setCurrentTabId] = useState<string>('insights');
  const [expandedCards, setExpandedCards] = useState<Record<number, boolean>>({});
  const navigation = useNavigation();
  const isAdminRoute = useAdminRoute();
  const { isMobile } = useScreen();
  const [{ data: personaData, fetching: fetchingPersonaData }] = usePersonaQuery({
    variables: {
      filters: {
        id: assetId,
      },
    },
    requestPolicy: 'cache-and-network',
  });

  const attemptIds =
    (personaData?.content as any)?.userSurveyAttemptConfig?.map(
      (attempt: any) => attempt?.attemptId,
    ) || [];

  const userSurveyIds = (personaData?.content as any)?.userSurveyAttemptConfig?.map(
    (attempt: any) => attempt?.userSurveyId,
  );

  const [{ data: personaDetailsData, fetching: fetchingPersonaDetailsData }] =
    usePersonaDetailsQuery({
      variables: {
        filters: {
          attemptIds,
          ...(isPreview ? { versionStatus: UserSurveyResponseVersionStatusEnum.Draft } : {}),
        },
        first: -1,
      },
      pause: !attemptIds?.length,
      requestPolicy: 'cache-and-network',
    });

  const [{ data: personaQuestionsData, fetching: fetchingUserSurveyDetailsData }] =
    usePersonaQuestionIdsQuery({
      variables: {
        filters: {
          ids: userSurveyIds,
        },
      },
      pause: !userSurveyIds?.length,
      requestPolicy: 'cache-and-network',
    });

  const questionIds =
    personaQuestionsData?.userSurveys?.edges?.flatMap(
      (edge) =>
        edge?.node?.questionConfig?.surveyQuestions?.map(
          (question) => question?.questionId || '',
        ) || [],
    ) || [];

  const [{ data: questionsData, fetching: fetchingQuestionsData }] = useQuestionsQuery({
    variables: {
      filters: {
        ids: questionIds,
      },
    },
    pause: !questionIds?.length,
    requestPolicy: 'cache-and-network',
  });

  const title = getResponseByQuestionText('Title', questionsData, personaDetailsData);
  const department = getResponseByQuestionText('Industry', questionsData, personaDetailsData);
  const role = getResponseByQuestionText('Persona Type', questionsData, personaDetailsData);
  const avatarUrl = getResponseByQuestionText('Persona Face', questionsData, personaDetailsData);

  const rawSolutionIds = getResponseByQuestionText(
    'Solutions',
    questionsData,
    personaDetailsData,
    true,
  );

  const solutionIds = (rawSolutionIds as string).split(',').map((id) => id.trim());
  const [{ data: solutionsData }] = useKnowledgeAssetsQuery({
    variables: {
      filters: {
        ids: solutionIds || [],
      },
    },
    requestPolicy: 'cache-and-network',
  });

  const personaCardData = {
    avatarUrl,
    title: title || 'Title',
    role,
    departments: [department],
  };

  const tabs = [
    {
      label: 'Insights',
      value: 'insights',
    },
    {
      label: 'Resolution',
      value: 'resolution',
    },
  ];

  const insightsQuestions = [
    'Responsibilities',
    'Top Concerns',
    'Aspirations',
    'Goals',
    'Pain Points',
  ];
  const resolutionQuestions: string[] = [];

  const isLoading =
    fetchingPersonaData ||
    fetchingPersonaDetailsData ||
    fetchingUserSurveyDetailsData ||
    fetchingQuestionsData;

  const pageTitle = (personaData?.content as any)?.name;

  const handleEditClick = useCallback(() => {
    navigation.replace(`/admin/knowledge-asset/persona/${assetId}/edit`);
  }, [assetId, navigation]);

  const onBack = useCallback(() => {
    navigation.replace(`/admin/knowledge-asset/persona`);
  }, [navigation]);

  usePageLayout({
    title: pageTitle,
    LeftNode: <TemplatesHeaderNode isPreview={isPreview} title={pageTitle} onBack={onBack} />,
    RightNode:
      isPreview || isMobile ? null : (
        <Button StartIcon={EditOutline} variant="outlined" onClick={handleEditClick}>
          Edit Persona
        </Button>
      ),
    showHeader: !isMobile,
  });

  const toggleCard = (index: number) => {
    setExpandedCards((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const getCardData = (): CardData[] => {
    const questions = currentTabId === 'insights' ? insightsQuestions : resolutionQuestions;

    return questions.map((questionText) => ({
      title: questionText,
      responses: parsePipeSeparatedString(
        getResponseByQuestionText(questionText, questionsData, personaDetailsData),
      ),
    }));
  };

  const onbackPress = useCallback(() => {
    // navigation.replace(routes.LibraryPortal(), { appendQuery: false });
    navigation.back();
  }, [navigation]);

  const getIconForCard = (title: string) => {
    switch (title) {
      case 'Responsibilities':
        return <HandWithCircleOutline fillColorToken="icon-active" height={24} width={24} />;
      case 'Top Concerns':
        return <FaceWithQuestionMark fillColorToken="icon-active" height={24} width={24} />;
      case 'Aspirations':
        return <RoundedStar fill="transparent" height={24} width={24} />;
      case 'Goals':
        return <TargetWithFlag fill="transparent" height={24} width={24} />;
      default:
        return <AllDoneFill fillColorToken="icon-active" height={24} width={24} />;
    }
  };

  if (isLoading) {
    return (
      <Stack alignItems="center" className="h-[calc(100vh-64px)] w-full" justifyContent="center">
        <Loader size={40} />
      </Stack>
    );
  }

  return (
    <Stack
      alignItems="center"
      className="mt-48 max-w-[900px] min-w-[900px] mx-auto xs:min-w-full xs:p-12 xs:mt-12"
      direction="col"
      gapY={32}>
      {isAdminRoute ? null : (
        <Stack className="w-full">
          <ArrowBackOutline className="h-24 w-24 cursor-pointer" onClick={onbackPress} />
        </Stack>
      )}
      <PersonaCard {...personaCardData} />
      <Stack className="self-start pl-24">
        <Tabs tabs={tabs} value={currentTabId} onChange={setCurrentTabId} />
      </Stack>
      <Stack className="grid grid-cols-2 gap-16 w-full xs:grid-cols-1">
        {getCardData().map((card, index) => {
          const isEmpty = !card.responses || card.responses.length === 0;

          return (
            <Stack
              key={index}
              className={`border border-border rounded-12 p-20 ${
                expandedCards[index] && !isEmpty ? '' : 'max-h-[224px]'
              } transition-all duration-300 relative`}
              direction="col"
              gapY={12}>
              <Stack alignItems="center" gapX={8}>
                {getIconForCard(card.title)}
                <Typography variant="heading-xs">{card.title}</Typography>
              </Stack>
              <Stack className="overflow-hidden relative" direction="col" gapY={8}>
                {isEmpty ? (
                  <EmptyState />
                ) : (
                  <>
                    {card.responses.map((response: string, responseIndex: number) => (
                      <TypographyWithBulletPoint
                        key={responseIndex}
                        className="text-text-secondary"
                        text={response}
                        variant="body-long-01"
                      />
                    ))}
                    {!expandedCards[index] && (
                      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
                    )}
                  </>
                )}
              </Stack>
              {!isEmpty && card.responses.length > 2 && (
                <Stack className="self-start mt-4 cursor-pointer" onClick={() => toggleCard(index)}>
                  <Stack alignItems="center" gapX={4}>
                    <Typography variant="heading-xxs">
                      {expandedCards[index] ? 'See less' : 'See more'}
                    </Typography>
                    <ArrowRightFill
                      className={`transform transition-transform duration-300 ${
                        expandedCards[index] ? '-rotate-90' : ''
                      }`}
                      height={16}
                      width={16}
                    />
                  </Stack>
                </Stack>
              )}
            </Stack>
          );
        })}
      </Stack>
      {currentTabId === 'resolution' && solutionsData?.contents?.edges?.length ? (
        <Stack className="self-start" direction="col" gapY={24}>
          <Typography className="pl-4" variant="display-s">
            Solutions
          </Typography>
          <Stack
            className="grid grid-cols-2 xs:grid-cols-1 gap-16"
            style={{ maxWidth: 'fit-content' }}>
            {solutionsData?.contents?.edges?.map((edge, index) => (
              <SolutionCardGrid
                key={index}
                index={index}
                solution={edge?.node as ContentsFragment}
              />
            ))}
          </Stack>
        </Stack>
      ) : null}
    </Stack>
  );
};

export default PersonaDetails;
